import Store from 'store/store'

import Footer from 'components/paper/Footer'
import Image from 'components/paper/Image'
import Logo from 'components/paper/Logo'
import Paragraph from 'components/paper/Paragraph'

import Landscape from 'controllers/landscape/render'

export default async (props, refs, paper, render) => {
  props.bannerHeight = (props.unit + props.padding * 1.2)
  props.unit *= 0.6

  // Landscape
  ;(() => {
    const landscape = Store.landscape.abstraction.get()
    if (!landscape) return

    const scale = Math.max(props.width / landscape.props.width, props.height / landscape.props.height)
    Landscape.render(paper, { landscape, scale })

    /**
     * As the SVG version of the Landscape is really heavy on the DOM when
     * rendering multiples formats at the same time, we use the previously
     * rendered canvas as a placeholder for preview.
     * This image will not be exported.
     * Hiding the real landscape in CSS using display:none seems good enough for
     * improving scrolling perf.
     */

    render('landscape', (
      <image
        class='landscape no-export'
        ref={paper.ref('landscapePlaceholder')}
        href={Store.landscape.image.current[props.colorSpace]}
        width={landscape.props.width * scale}
        height={landscape.props.height * scale}
      />
    ))

    paper.refs.horizontalOffset.state.disabled.set(landscape.props.width * scale <= props.width)
    paper.transformLandscape()
  })()

  // Cover
  Store.cover.current && (() => {
    const ratio = Store.cover.current.width / Store.cover.current.height
    const position = props.orientation === 'portrait'
      ? {
        x: 0,
        y: 0, // props.bannerHeight,
        width: props.width,
        height: Math.min(props.width / ratio, props.height / 2)
      } : {
        x: props.width / 2,
        y: 0,
        width: props.width / 2,
        height: props.height - (refs.footer ? refs.footer.state.rectangle.height : 0)
      }

    render('cover', (
      <Image
        {...position}
        preserveAspectRatio='xMidYMid slice'
        href={Store.cover.current.src}
        data-filename={Store.cover.current.name}
      />
    ))
  })()

  // Header
  ;(() => {
    render('header', (
      <rect
        {...props.background}
        x={0}
        y={0}
        width={props.orientation === 'portrait' ? '100%' : '50%'}
        height={props.bannerHeight}
      />
    ))

    render('logo', (
      <Logo
        color={props.foreground}
        origin='NE'
        x={
          props.orientation === 'portrait'
            ? props.width - props.padding
            : (props.width / 2) - props.padding
        }
        y={props.padding * 0.6}
        height={props.unit * 1.4}
      />
    ))

    render('title', (
      <Paragraph
        x={props.padding}
        y={props.padding * 0.7}
        width={refs.logo.state.rectangle.x - props.padding * 2}
        fontSize={props.unit}
        color={props.foreground}
      >
        Expérience(s) de paysage
      </Paragraph>
    ))

    if (refs.title.refs.lines.length > 1) {
      refs.title.destroy()
      render('title', (
        <Paragraph
          x={props.padding}
          y={props.padding * 0.6}
          width={refs.logo.state.rectangle.x - props.padding * 2}
          height={props.bannerHeight - props.padding * 2}
          fontSize={props.unit * 0.6}
          color={props.foreground}
        >
          {'Expérience(s)\nde paysage'}
        </Paragraph>
      ))
    }
  })()

  // Footer
  props.hasLogos && render('footer', (
    <Footer
      y={props.height}
      origin='SW'
      width={props.width}
      height={props.bannerHeight}
      padding={[props.padding, props.padding * 0.6]}
      fill='white'
      logos={Store.logos.get()}
    />
  ))

  // Texts
  !Store.poster.hasNoTexts.get() && (() => {
    // Short-circuit maxFontSize computing if user has touched the fontScale slider
    const fontSize = paper.state.fontScale.get()
    if (fontSize) return iterate(fontSize)

    // Find the best layout, starting for a maxFontSize and decrementing
    let iter = 0
    let maxFontSize = Store.constants.current.MAX_FONT_SIZE
    while (maxFontSize > Store.constants.current.MIN_FONT_SIZE && ++iter < 99) {
      iterate()
      maxFontSize = refs.h1.state.fontSize

      // [h1] only: no further iterations
      if (!refs.h2.state.value && !refs.h3.state.value) break
      // [h1 + h2] only: h2 does not overflow height
      if (!refs.h3.state.value && refs.h2.state.bbox.ymax + props.padding < props.height) break
      // [h1 + h3] only: no break rule necessary
      // [h1 + h2 + h3]: h2 does not overlap h3
      if (refs.h2.state.bbox.ymax + props.padding < refs.h3.state.bbox.ymin) break

      maxFontSize -= 1
      if (maxFontSize <= 0) {
        iterate(Store.constants.current.MIN_FONT_SIZE)
        break
      }
    }

    // Update the fontScale slider to use the computed maxFontSize by default
    paper.refs.fontScale.state.value.set(maxFontSize)
    paper.refs.fontScale.state.defaultValue.set(maxFontSize)

    paper.log({ iter, maxFontSize })

    function iterate (fontSize = null) {
      const common = {
        x: props.padding,
        color: props.foreground
      }

      ;[refs.h1, refs.h2, refs.h3].forEach(ref => ref && ref.destroy())

      render('h1', (
        <Paragraph
          {...common}
          y={
            props.orientation === 'portrait'
              ? (refs.cover?.state.rectangle.bottom ?? props.bannerHeight) + props.padding
              : refs.logo.state.rectangle.bottom + props.padding
          }
          width={
            props.orientation === 'portrait'
              ? props.width - props.padding * 2
              : (props.width / 2) - props.padding * 2
          }
          height={props.orientation === 'portrait'
            ? props.height - (refs.cover?.state.rectangle.height ?? props.bannerHeight) - props.padding * 2
            : props.height - props.bannerHeight - props.padding * 2}
          lineGap={0.95}
          fontSize={fontSize}
          maxFontSize={maxFontSize}
        >
          {Store.text.h1.get()}
        </Paragraph>
      ))

      render('h2', (
        <Paragraph
          {...common}
          y={refs.h1.state.bbox.ymax + (fontSize || refs.h1.state.fontSize) * 0.5}
          width={
            props.orientation === 'portrait'
              ? props.width - props.padding * 2
              : (props.width / 2) - props.padding * 2
          }
          height={props.height - refs.h1.state.bbox.ymax - (fontSize || refs.h1.state.fontSize) * 0.5 - props.padding}
          // Instead of setting a hard fontSize (line refs.h1.state.fontSize / 2)
          // we give a small range to allow for wiggle room when using arnaudjuracek/drapeau
          fontSize={fontSize && (fontSize * 0.5)}
          minFontSize={refs.h1.state.fontSize * 0.4}
          maxFontSize={refs.h1.state.fontSize * 0.6}
        >
          {Store.text.h3.get()}
        </Paragraph>
      ))

      render('h3', (
        <Paragraph
          {...common}
          origin='SW'
          y={props.hasLogos
            ? props.height - props.padding - refs.footer.state.rectangle.height
            : props.height - props.padding}
          width={
            props.orientation === 'portrait'
              ? props.width - props.padding * 2
              : props.width / 2 - props.padding * 2
          }
          height={props.height - props.padding - refs.h2.state.bbox.ymax}
          // Instead of setting a hard fontSize (line refs.h1.state.fontSize / 4)
          // we give a small range to allow for wiggle room and using arnaudjuracek/drapeau
          minFontSize={refs.h1.state.fontSize * 0.3}
          maxFontSize={refs.h1.state.fontSize * 0.4}
        >
          {[
            Store.text.h4.get(),
            Store.text.h5.get(),
            Store.text.h6.get()
          ].join('\n')}
        </Paragraph>
      ))
    }
  })()
}



// WEBPACK FOOTER //
// ./src/controllers/templates/cover.js